import { useState, useEffect, useRef } from 'react';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;

// This is a client-side SPA: once a tab has loaded the JS, it runs entirely
// from memory and never re-fetches new code on its own after a deploy --
// only a real reload does. CRA already regenerates asset-manifest.json with
// a fresh content-hashed main.js on every build, so it doubles as a free
// version marker; no new build step needed. Checks on an interval and
// whenever the tab regains focus, since "gestor comes back after a while"
// is exactly the moment a stale tab is most likely.
export const useVersionCheck = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const currentMainJs = useRef(null);

  useEffect(() => {
    const fetchManifest = async () => {
      try {
        const res = await fetch(`/asset-manifest.json?_=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return null;
        const data = await res.json();
        return data.files?.['main.js'] || null;
      } catch {
        return null;
      }
    };

    const checkForUpdate = async () => {
      const latest = await fetchManifest();
      if (!latest) return;
      if (currentMainJs.current === null) {
        currentMainJs.current = latest;
        return;
      }
      if (latest !== currentMainJs.current) setUpdateAvailable(true);
    };

    checkForUpdate();
    const interval = setInterval(checkForUpdate, CHECK_INTERVAL_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkForUpdate();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  return updateAvailable;
};
